import React, { useState, useCallback } from 'react';
import {
  Box,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Flex,
  Icon,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { FiPaperclip } from 'react-icons/fi';
import { useSWRConfig } from 'swr';
import ConfirmActionModal from './ConfirmActionModal';
import RefundFormLabel from './RefundFormLabel';
import StatusBadge from './StatusBadge';
import BankDetailsDisplay from './BankDetailsDisplay';
import PaymentInvoicingNotice from './PaymentInvoicingNotice';
import {
  markRetained,
  proceedRefund,
  completeRefund,
  uploadRefundFile,
  formatCOPDisplay,
  formatRefundDateTime,
  getStudentDisplayName,
  getUserDisplayName,
} from 'api/administrador/refunds';

const PURPLE = '#574feb';
const GREEN = '#22A06B';

function PanelHeader({ children }) {
  return (
    <Box bg={PURPLE} px={6} py={3} borderTopRadius="lg">
      <Text color="white" fontWeight="bold" fontSize="md">
        {children}
      </Text>
    </Box>
  );
}

function FieldLabel({ children, required = false }) {
  return <RefundFormLabel required={required}>{children}</RefundFormLabel>;
}

function ClosedCasePanel({ refund }) {
  const isRetained = refund.status === 'retained';
  const closingEvent = [...(refund.events || [])]
    .filter((event) =>
      isRetained ? event.action === 'marked_retained' : event.action === 'completed'
    )
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  const closedBy = getUserDisplayName(closingEvent?.user || refund.registered_by);
  const closedAt = formatRefundDateTime(closingEvent?.createdAt || refund.updatedAt);

  return (
    <Box bg="white" borderRadius="lg" border="1px solid #9e46d8" overflow="hidden">
      <PanelHeader>Caso cerrado</PanelHeader>
      <Box px={6} py={5}>
        <Flex direction="column" align="center" textAlign="center" mb={5}>
          <Flex
            w="48px"
            h="48px"
            borderRadius="full"
            bg={isRetained ? 'green.50' : '#E8E4FC'}
            color={isRetained ? GREEN : PURPLE}
            align="center"
            justify="center"
            fontSize="xl"
            fontWeight="bold"
            mb={3}
          >
            ✓
          </Flex>
          <StatusBadge status={refund.status} />
          <Text fontWeight="bold" fontSize="md" mt={3} color="gray.800">
            {isRetained ? 'Estudiante retenido' : 'Devolución completada'}
          </Text>
          <Text fontSize="sm" color="gray.500" mt={2} lineHeight="tall" maxW="320px">
            {isRetained
              ? 'El estudiante continúa en el programa. No se procesará devolución.'
              : 'La transferencia fue confirmada y el caso quedó cerrado.'}
          </Text>
        </Flex>

        <Box bg="gray.50" borderRadius="md" p={4}>
          <Flex justify="space-between" align="flex-start" gap={4} mb={3}>
            <Text fontSize="sm" color="gray.600" flexShrink={0}>
              Estudiante
            </Text>
            <Text fontSize="sm" fontWeight="medium" color="gray.800" textAlign="right">
              {getStudentDisplayName(refund.student)}
            </Text>
          </Flex>
          {!isRetained && (
            <Flex justify="space-between" align="flex-start" gap={4} mb={3}>
              <Text fontSize="sm" color="gray.600" flexShrink={0}>
                Monto devuelto
              </Text>
              <Text fontSize="sm" fontWeight="bold" color={PURPLE} textAlign="right">
                {formatCOPDisplay(refund.refund_amount)}
              </Text>
            </Flex>
          )}
          <Flex justify="space-between" align="flex-start" gap={4} mb={3}>
            <Text fontSize="sm" color="gray.600" flexShrink={0}>
              Cerrado por
            </Text>
            <Text fontSize="sm" color="gray.800" textAlign="right">
              {closedBy}
            </Text>
          </Flex>
          <Flex justify="space-between" align="flex-start" gap={4}>
            <Text fontSize="sm" color="gray.600" flexShrink={0}>
              Fecha de cierre
            </Text>
            <Text fontSize="sm" color="gray.800" textAlign="right">
              {closedAt}
            </Text>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}

export default function CaseActionPanel({ refund, onUpdated }) {
  const toast = useToast();
  const { mutate } = useSWRConfig();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [note, setNote] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refundDate, setRefundDate] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptUploaded, setReceiptUploaded] = useState(!!refund.transfer_receipt);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const onDropReceipt = useCallback(
    async (acceptedFiles) => {
      const f = acceptedFiles[0];
      if (!f) return;

      if (f.size > 5 * 1024 * 1024) {
        toast({ title: 'El archivo no puede superar 5 MB', status: 'warning', duration: 4000 });
        return;
      }

      setReceiptFile(f);
      setUploadingReceipt(true);
      try {
        await uploadRefundFile({ file: f, refId: refund.id, field: 'transfer_receipt' });
        setReceiptUploaded(true);
        toast({ title: 'Comprobante cargado', status: 'success', duration: 3000 });
      } catch {
        toast({ title: 'Error al cargar comprobante', status: 'error', duration: 4000 });
        setReceiptFile(null);
        setReceiptUploaded(false);
      } finally {
        setUploadingReceipt(false);
      }
    },
    [refund.id, toast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropReceipt,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    maxFiles: 1,
  });

  const clearReceipt = () => {
    setReceiptFile(null);
    setReceiptUploaded(false);
  };

  const openConfirm = (action) => {
    setPendingAction(action);
    onOpen();
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      if (pendingAction === 'proceed') {
        await proceedRefund(refund.id, note || undefined);
        toast({ title: 'Caso pasó a listo para pago', status: 'success', duration: 4000 });
      } else {
        await markRetained(refund.id, note || undefined);
        toast({ title: 'Caso marcado como retenido', status: 'success', duration: 4000 });
      }
      mutate('statsAdmin');
      onClose();
      onUpdated?.();
    } catch (error) {
      const message = error?.response?.data?.error?.message || 'Error al procesar la acción';
      toast({ title: message, status: 'error', duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!refundDate) {
      toast({ title: 'La fecha de devolución es obligatoria', status: 'warning', duration: 4000 });
      return;
    }
    if (!receiptUploaded) {
      toast({ title: 'Debes cargar el comprobante de transferencia', status: 'warning', duration: 4000 });
      return;
    }

    setIsLoading(true);
    try {
      await completeRefund(refund.id, refundDate);
      toast({ title: 'Devolución completada', status: 'success', duration: 4000 });
      mutate('statsAdmin');
      onUpdated?.();
    } catch (error) {
      const message = error?.response?.data?.error?.message || 'Error al completar devolución';
      toast({ title: message, status: 'error', duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  const receiptName =
    receiptFile?.name || refund.transfer_receipt?.name || 'comprobante_transferencia.pdf';

  if (refund.status === 'completed' || refund.status === 'retained') {
    return <ClosedCasePanel refund={refund} />;
  }

  if (refund.status === 'pending') {
    return (
      <>
        <Box bg="white" borderRadius="lg" border="1px solid #9e46d8" overflow="hidden">
          <PanelHeader>Éxito del estudiante — Retención</PanelHeader>
          <Box px={6} py={5}>
            <FormControl mb={5}>
              <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                Notas de retención
              </FormLabel>
              <Textarea
                placeholder="Documentar llamadas, acuerdos, intentos de retención, motivos del estudiante..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                borderColor="gray.300"
                _focus={{ borderColor: PURPLE, boxShadow: `0 0 0 1px ${PURPLE}` }}
              />
              <Text fontSize="xs" color="gray.500" fontStyle="italic" mt={2}>
                *Estas notas quedan en el historial del caso.*
              </Text>
            </FormControl>

            <Box mb={5}>
              <Button
                w="full"
                bg={GREEN}
                color="white"
                fontWeight="bold"
                size="md"
                py={6}
                _hover={{ bg: '#1E8F5F' }}
                onClick={() => openConfirm('retain')}
              >
                ✓ Estudiante retenido
              </Button>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Cierra el caso. El estudiante continúa en el programa. No se procesará devolución.
              </Text>
            </Box>

            <Box>
              <Button
                w="full"
                bg="gray.700"
                color="white"
                fontWeight="bold"
                size="md"
                py={6}
                _hover={{ bg: 'gray.800' }}
                onClick={() => openConfirm('proceed')}
              >
                No se logró retener → Procede devolución
              </Button>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Pasa el caso a la cola de &quot;Listo para pago&quot;. El equipo revisará y subirá el
                comprobante de transferencia.
              </Text>
            </Box>
          </Box>
        </Box>

        <ConfirmActionModal
          isOpen={isOpen}
          onClose={onClose}
          action={pendingAction}
          studentName={getStudentDisplayName(refund.student)}
          studentCode={refund.student?.id}
          refundAmount={refund.refund_amount}
          onConfirm={handleConfirm}
          isLoading={isLoading}
        />
      </>
    );
  }

  if (refund.status === 'ready_for_payment') {
    return (
      <Box bg="white" borderRadius="lg" border="1px solid #9e46d8" overflow="hidden">
        <PanelHeader>Revisión y comprobante</PanelHeader>
        <Box px={6} py={5}>
          <PaymentInvoicingNotice payment={refund.payment} />
          <BankDetailsDisplay refund={refund} />

          <FormControl isRequired mb={5}>
            <FieldLabel required>Fecha de devolución</FieldLabel>
            <Input
              type="date"
              value={refundDate}
              onChange={(e) => setRefundDate(e.target.value)}
              borderColor="gray.300"
              _focus={{ borderColor: PURPLE, boxShadow: `0 0 0 1px ${PURPLE}` }}
            />
          </FormControl>

          <FormControl isRequired mb={5}>
            <FieldLabel required>Comprobante de transferencia</FieldLabel>
            <Box
              {...getRootProps()}
              border="2px dashed"
              borderColor={isDragActive ? PURPLE : '#C4B5FD'}
              borderRadius="md"
              p={6}
              textAlign="center"
              cursor="pointer"
              bg={isDragActive ? '#574feb08' : '#FAFAFF'}
            >
              <input {...getInputProps()} />
              <Icon as={FiPaperclip} boxSize={5} color="gray.400" mb={2} />
              <Text fontSize="sm">
                <Text as="span" color={PURPLE} fontWeight="semibold">
                  Selecciona archivo
                </Text>{' '}
                <Text as="span" color="gray.600">
                  o arrastra aquí
                </Text>
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                PDF, PNG o JPG · máx 5 MB
              </Text>
            </Box>

            {(receiptFile || refund.transfer_receipt) && receiptUploaded && (
              <Flex
                mt={3}
                align="center"
                justify="space-between"
                gap={3}
                px={3}
                py={2}
                bg="green.50"
                border="1px solid"
                borderColor="green.200"
                borderRadius="md"
              >
                <Flex align="center" gap={2} minW={0}>
                  <Text color="green.600" fontWeight="bold">
                    ✓
                  </Text>
                  <Text fontSize="sm" color="gray.700" noOfLines={1}>
                    {receiptName}
                  </Text>
                </Flex>
                <Button
                  size="xs"
                  variant="ghost"
                  color="red.500"
                  minW="auto"
                  onClick={clearReceipt}
                  isDisabled={uploadingReceipt}
                >
                  ✕
                </Button>
              </Flex>
            )}
          </FormControl>

          <Button
            w="full"
            bg={PURPLE}
            color="white"
            fontWeight="bold"
            size="md"
            py={6}
            _hover={{ bg: '#4840d4' }}
            onClick={handleComplete}
            isLoading={isLoading || uploadingReceipt}
          >
            Confirmar devolución completada
          </Button>
          <Text fontSize="xs" color="gray.500" mt={3} lineHeight="tall">
            Esta acción cierra el caso, marca el estado como <Text as="span" fontWeight="bold">Completada</Text>{' '}
            y notifica al equipo. No es reversible.
          </Text>
        </Box>
      </Box>
    );
  }

  return null;
}
